"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ELASTIC_POSTS_PER_PAGE = void 0;
exports.default = postElasticSearch;
/** SearchX search types, see MangoPulseSearchTypes in the SearchX repo. */
const SEARCH_TYPE_BY_SCOPE = {
    all: 'title_and_relatives', // title + summary + content + category + author
    title: 'exact_title', // title fields only
    text: 'content_only', // summary + content only
};
const FALLBACK_SEARCH_TYPE = SEARCH_TYPE_BY_SCOPE.all;
/** SearchX pages with from/size; keep this in step with getNextPageParam in useSearch. */
exports.ELASTIC_POSTS_PER_PAGE = 20;
/**
 * SearchX wants "yyyy-MM-dd HH:mm:ss" (a space, not the ISO "T"), and parses it with a
 * plain SimpleDateFormat, so anything else throws server-side. Callers pass either a bare
 * date or the ISO-ish form the CMS endpoint uses.
 */
function toElasticDate(value, endOfDay = false) {
    if (!value || typeof value !== 'string')
        return undefined;
    const [datePart, timePart] = value.trim().split('T');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart))
        return undefined;
    // A "to" of T00:00:00 would drop the whole final day, which is not what a
    // date-range picker means by "up to and including".
    const time = endOfDay ? '23:59:59' : (timePart || '00:00:00').slice(0, 8);
    return `${datePart} ${time}`;
}
function buildBody(config, options, pageParam) {
    var _a, _b, _c, _d;
    const size = options.postsPerPage || exports.ELASTIC_POSTS_PER_PAGE;
    const filters = {};
    if (options.authors)
        filters.author = [options.authors];
    if (options.categories)
        filters.category = [options.categories];
    if ((_a = options.includePostTypes) === null || _a === void 0 ? void 0 : _a.length)
        filters.postType = options.includePostTypes;
    const excludeFilters = {};
    // Restricting to a postType already says everything an exclusion could; applying both
    // would only let them contradict each other.
    if (!((_b = options.includePostTypes) === null || _b === void 0 ? void 0 : _b.length) && ((_c = options.excludePostTypes) === null || _c === void 0 ? void 0 : _c.length))
        excludeFilters.postType = options.excludePostTypes;
    return Object.assign(Object.assign({ userId: config.CLIENT_ID, searchText: (_d = options.q) === null || _d === void 0 ? void 0 : _d.trim(), size, from: Math.max(0, (pageParam || 1) - 1) * size, fromDate: toElasticDate(options.from), toDate: toElasticDate(options.to, true) }, (Object.keys(filters).length ? { filters } : {})), (Object.keys(excludeFilters).length ? { excludeFilters } : {}));
}
/** SearchX returns its own document shape; re-wrap it as the envelope searchModel expects. */
function toSearchEnvelope(raw) {
    var _a, _b, _c, _d;
    return {
        count: (_a = raw === null || raw === void 0 ? void 0 : raw.count) !== null && _a !== void 0 ? _a : 0,
        postTypeCounts: (_c = (_b = raw === null || raw === void 0 ? void 0 : raw.filters) === null || _b === void 0 ? void 0 : _b.postType) !== null && _c !== void 0 ? _c : null,
        posts: ((_d = raw === null || raw === void 0 ? void 0 : raw.data) !== null && _d !== void 0 ? _d : []).map((doc) => {
            var _a, _b, _c, _d, _e, _f, _g;
            return ({
                id: (_a = doc === null || doc === void 0 ? void 0 : doc.id) !== null && _a !== void 0 ? _a : '',
                title: (_b = doc === null || doc === void 0 ? void 0 : doc.title) !== null && _b !== void 0 ? _b : '',
                summary: (_c = doc === null || doc === void 0 ? void 0 : doc.summary) !== null && _c !== void 0 ? _c : '',
                publicUrl: (_d = doc === null || doc === void 0 ? void 0 : doc.publicUrl) !== null && _d !== void 0 ? _d : '',
                postType: (_e = doc === null || doc === void 0 ? void 0 : doc.postType) !== null && _e !== void 0 ? _e : '',
                thumbnail: (_f = doc === null || doc === void 0 ? void 0 : doc.thumbnail) !== null && _f !== void 0 ? _f : '',
                author: (doc === null || doc === void 0 ? void 0 : doc.author) ? { name: doc.author } : undefined,
                tag: (doc === null || doc === void 0 ? void 0 : doc.category) ? { title: doc.category } : undefined,
                // formatArabicDate matches "yyyy-MM-dd HH:mm" and SearchX sends seconds too.
                displayDate: (doc === null || doc === void 0 ? void 0 : doc.date) ? String(doc.date).slice(0, 16) : '',
                date: (_g = doc === null || doc === void 0 ? void 0 : doc.date) !== null && _g !== void 0 ? _g : null,
            });
        }),
    };
}
async function requestSearch(searchUrl, type, relevance, body) {
    const response = await fetch(`${searchUrl}/search?type=${type}&relevance=${relevance}`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json;charset=UTF-8' },
        body: JSON.stringify(body),
    });
    if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
    return response.json();
}
/**
 * Search articles straight against SearchX rather than through the CMS search proxy.
 * The proxy pins the search type to title_and_relatives, so it cannot express
 * "title only" or "text only" — this is the path the advanced-search options need.
 */
async function postElasticSearch(config, options, pageParam) {
    const searchUrl = config.SEARCH_URL;
    if (!searchUrl)
        throw new Error('SEARCH_URL is not configured');
    const scope = options.searchScope || 'all';
    const type = SEARCH_TYPE_BY_SCOPE[scope] || FALLBACK_SEARCH_TYPE;
    // orderBy 1 means "most relevant"; 0 keeps the newest-first order the site has today.
    const relevance = options.orderBy === 1;
    const body = buildBody(config, options, pageParam || 1);
    try {
        return toSearchEnvelope(await requestSearch(searchUrl, type, relevance, body));
    }
    catch (error) {
        // A SearchX build that predates content_only rejects it outright. Degrading to the
        // combined type shows the user results rather than an empty page.
        if (type === FALLBACK_SEARCH_TYPE)
            throw error;
        return toSearchEnvelope(await requestSearch(searchUrl, FALLBACK_SEARCH_TYPE, relevance, body));
    }
}
